import type { TicketContext } from '@traceio-org/platform-contracts';
import { ScrollTable } from '../../components/records';
import { formatDateTime, productName } from '../../format';

function formatMs(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
}

function describeModuleState(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * What the Get help popover captured with the ticket (only Trace and the customer's help desk see it): the app and
 * page (a route template, never the customer's ids), the browser and screen, the last requests (failed ones
 * highlighted, with their request ids for the logs), the last front-end errors and the module states at that moment.
 */
export function TicketContextPanel({ context, capturedAt }: { context: TicketContext; capturedAt: string }) {
  const failed = context.requests.filter((r) => r.status >= 400).length;
  const modules = Object.entries(context.moduleStates);
  return (
    <section className="admin-ticket-context" aria-labelledby="ticket-context-title">
      <h2 id="ticket-context-title" className="admin-ticket-context__title">
        Captured context <span className="admin-muted">· {formatDateTime(capturedAt)}</span>
      </h2>
      <dl className="admin-ticket-context__facts">
        <div>
          <dt>App · page</dt>
          <dd>
            {productName(context.app)} · <code>{context.url}</code>
          </dd>
        </div>
        <div>
          <dt>Browser</dt>
          <dd>
            {context.browser || 'Unknown'} · {context.viewport.width}×{context.viewport.height}
          </dd>
        </div>
      </dl>
      {context.requests.length > 0 ? (
        <ScrollTable
          caption={`Last requests (${context.requests.length}), ${failed} failed`}
          className="admin-table admin-ticket-requests"
        >
          <thead>
            <tr>
              <th scope="col">Method</th>
              <th scope="col">Route</th>
              <th scope="col">Status</th>
              <th scope="col">Duration</th>
              <th scope="col">Request id</th>
            </tr>
          </thead>
          <tbody>
            {context.requests.map((request, index) => (
              <tr key={`${request.requestId}-${index}`} data-failed={request.status >= 400 || undefined}>
                <td>{request.method}</td>
                <td>
                  <code>{request.route}</code>
                </td>
                <td>
                  {request.status}
                  {request.status >= 400 ? <span className="admin-sr-only"> (failed)</span> : null}
                </td>
                <td>{formatMs(request.durationMs)}</td>
                <td>
                  <code className="admin-id">{request.requestId}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      ) : null}
      {context.errors.length > 0 ? (
        <ul className="admin-bare-list admin-ticket-errors" aria-label="Front-end errors">
          {context.errors.map((error, index) => (
            <li key={index}>
              <code>
                {error.message}
                {error.frame ? ` · ${error.frame}` : ''}
              </code>
            </li>
          ))}
        </ul>
      ) : null}
      {modules.length > 0 ? (
        <p className="admin-muted admin-ticket-modules">
          Modules at the time:{' '}
          {modules.map(([key, value]) => `${key} (${describeModuleState(value)})`).join(' · ')}
        </p>
      ) : null}
    </section>
  );
}
